import type { TimeValue } from 'react-aria-components';
import {
  DateInput,
  DateSegment,
  Label,
  Text,
  TimeField as AriaTimeField,
} from 'react-aria-components';

import { type SelectSize, sizes } from '@/lib/base/forms/select/select';
import { cx } from '@/utils/cx';

interface TimeFieldProps {
  /** The current time value */
  value?: TimeValue | null;
  /** Default value for uncontrolled usage */
  defaultValue?: TimeValue | null;
  /** Callback when the time changes */
  onChange?: (value: TimeValue | null) => void;
  /** Label for the time field */
  label?: string;
  /** Accessible label when no visible label is provided */
  'aria-label'?: string;
  /** Description text below the field */
  description?: string;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Whether to use 24-hour format */
  hourCycle?: 12 | 24;
  /** Granularity of the time field */
  granularity?: 'hour' | 'minute' | 'second';
  /** Additional class name */
  className?: string;
  /**
   * Field size, on the shared field-control scale. Defaults to `'sm'` (40px)
   * so a time field matches the Input/Select beside it.
   */
  size?: SelectSize;
}

/**
 * A styled time field component using React Aria TimeField.
 * Matches UntitledUI design system styling.
 */
export function TimeField({
  value,
  defaultValue,
  onChange,
  label,
  'aria-label': ariaLabel,
  description,
  isDisabled = false,
  hourCycle = 12,
  granularity = 'minute',
  className,
  size = 'sm',
}: TimeFieldProps) {
  return (
    <AriaTimeField
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      isDisabled={isDisabled}
      hourCycle={hourCycle}
      granularity={granularity}
      aria-label={ariaLabel}
      className={cx('flex flex-col gap-1', className)}
    >
      {label && (
        <Label className='text-sm font-medium text-secondary'>{label}</Label>
      )}
      <DateInput
        className={cx(
          // `ring-1 ring-inset`, not `border` — a border occupies the layout
          // box, so an otherwise identical field rendered 2px taller than every
          // other control in lib/base/forms.
          'flex items-center gap-0.5 rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset',
          'text-primary',
          sizes[size].root,
          'focus-within:ring-2 focus-within:ring-brand',
          'transition-colors duration-150',
          isDisabled && 'opacity-50 cursor-not-allowed bg-secondary'
        )}
      >
        {segment => (
          <DateSegment
            segment={segment}
            className={cx(
              'rounded px-0.5 tabular-nums outline-none',
              'focus:bg-brand-solid focus:font-medium focus:text-fg-white focus:outline-hidden dark:focus:bg-brand-solid dark:focus:text-fg-white',
              segment.isPlaceholder && 'text-tertiary'
            )}
          />
        )}
      </DateInput>
      {description && (
        <Text slot='description' className='text-xs text-tertiary'>
          {description}
        </Text>
      )}
    </AriaTimeField>
  );
}
